import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingControllerApi } from '../api/generated';
import type { GetBooking200Response, UpdateBookingRequest } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { getApiErrorMessage } from '../utils/errorMessageUtils';

export interface UseBookingsResult<TRequest, TResponse> {
  bookings: TResponse[];
  isLoading: boolean;
  loadError: string | null;
  locationId: string | null;
  refresh: () => Promise<void>;
  createBooking: (payload: TRequest) => Promise<void>;
  updateBooking: (bookingId: string, payload: TRequest) => Promise<void>;
  deleteBooking: (bookingId: string) => Promise<void>;
}

/**
 * Shared CRUD logic for a single booking variant. Each concrete booking hook is a thin wrapper
 * that supplies its request/response types and the discriminant `bookingType` used to filter the
 * mixed booking list returned by the API.
 */
export function useBookings<
  TRequest extends UpdateBookingRequest,
  TResponse extends GetBooking200Response
>(bookingType: string, locationId: string | null): UseBookingsResult<TRequest, TResponse> {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<TResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const api = useMemo(() => new BookingControllerApi(createApiConfiguration()), []);

  const loadBookings = useCallback(
    async (locId: string) => {
      const response = await api.listBookings({ locationId: locId });
      setBookings(response.data.filter((b): b is TResponse => b.type === bookingType));
    },
    [api, bookingType]
  );

  // Refresh the list after a successful write. A refetch failure must not be reported as a write
  // failure (the booking was already persisted), so it surfaces through loadError instead.
  const refreshAfterMutation = useCallback(
    async (locId: string) => {
      try {
        await loadBookings(locId);
        setLoadError(null);
      } catch (e) {
        setLoadError(getApiErrorMessage(e, t('fetch-error')));
      }
    },
    [loadBookings, t]
  );

  const refresh = useCallback(async () => {
    if (!locationId) {
      setBookings([]);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      await loadBookings(locationId);
    } catch (e) {
      setLoadError(getApiErrorMessage(e, t('fetch-error')));
    } finally {
      setIsLoading(false);
    }
  }, [locationId, loadBookings, t]);

  useEffect(() => {
    if (!locationId) return;
    refresh().catch(() => {
      // load errors are already surfaced via loadError state
    });
  }, [locationId, refresh]);

  const createBooking = useCallback(
    async (payload: TRequest) => {
      if (!locationId) throw new Error('No location available');
      await api.createBooking({ locationId, updateBookingRequest: payload });
      await refreshAfterMutation(locationId);
    },
    [api, locationId, refreshAfterMutation]
  );

  const updateBooking = useCallback(
    async (bookingId: string, payload: TRequest) => {
      if (!locationId) throw new Error('No location available');
      await api.updateBooking({ locationId, bookingId, updateBookingRequest: payload });
      await refreshAfterMutation(locationId);
    },
    [api, locationId, refreshAfterMutation]
  );

  const deleteBooking = useCallback(
    async (bookingId: string) => {
      if (!locationId) throw new Error('No location available');
      await api.deleteBooking({ locationId, bookingId });
      await refreshAfterMutation(locationId);
    },
    [api, locationId, refreshAfterMutation]
  );

  return {
    bookings,
    isLoading,
    loadError,
    locationId,
    refresh,
    createBooking,
    updateBooking,
    deleteBooking
  };
}
